<template>
  <BaseEditor
    data-test-id="editEmailTemplate"
    :retain-focus="false"
    v-bind="$attrs"
    v-model="store.showEditDialog"
    :table-store-id="TableStoreId.EmailTemplates"
    :title="
      appStore.isProgramAdmin
        ? t('dialogs.headers.editEmailTemplate')
        : t('dialogs.headers.viewEmailTemplate')
    "
    :max-width="1100"
    @save-item="saveItem"
    :allow-edit="appStore.isProgramAdmin"
  >
    <v-form :fast-fail="false" @submit.prevent ref="editEmailTemplateForm">
      <v-container fluid>
        <PrimaryTextField
          :mode="appStore.isProgramAdmin ? EditorMode.Edit : EditorMode.View"
          :disabled="!appStore.isProgramAdmin"
          required
          :label="t('labels.subject')"
          :maxlength="255"
          v-model.trim="item.subject"
          :rules="RulesManager.getRules(Rule.Required)"
        />
        <PrimarySwitch
          v-if="appStore.isProgramAdmin"
          :label="item.enabled ? t('labels.emailEnabled') : t('labels.emailNotEnabled')"
          v-model.trim="item.enabled"
        />
        <DisplayText
          v-else
          :label="t('labels.status')"
          :value="item.enabled ? t('labels.emailEnabled') : t('labels.emailNotEnabled')"
        />
        <div style="max-width: 1000px">
          <RichTextEditor v-model="item.content" :rules="RulesManager.getRules(Rule.Required)" />
        </div>
      </v-container>
    </v-form>
  </BaseEditor>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { useRulesManager } from '@/composables/RulesManager'
import { EditorMode } from '@/enums/EditorMode'
import { Rule } from '@/enums/Rule'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEmailTemplatesStore } from '@/interfaces/api/IEmailTemplatesStore'
import { EmailTemplateRecord } from '@/models/EmailTemplateRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useAppStore } from '@/stores/ui/AppStore'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DisplayText from '../DisplayText.vue'

const appStore = useAppStore()
const RulesManager = useRulesManager()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES
const editEmailTemplateForm = ref<any>()
const store = TableStoreFactory.get(TableStoreId.EmailTemplates) as IEmailTemplatesStore

// COMPUTED PROPERTIES
const item = computed(() => {
  if (!store.selectedItem) {
    return new EmailTemplateRecord()
  }
  return store.selectedItem as EmailTemplateRecord
})

// LIFECYCLE HOOKS

// WATCHERS

// FUNCTIONS
const saveItem = async () => {
  const { valid } = await editEmailTemplateForm.value.validate()
  if (!valid) {
    return
  }

  let item = { ...store.selectedItem }
  item.content = toPlainHTML(item.content)
  if (item.isDefault) {
    item.id = undefined
    item.isDefault = false
  }
  await store.save(true, false, true, item)
}

const toPlainHTML = (htmlText: string) => {
  const pattern =
    /<span class="variable"[^>]*data-original-variable="({{\s*\w+\s*}})"[^>]*>.*?<\/span>/g
  return htmlText.replace(pattern, (_, variable) => variable)
}
</script>

<style lang="scss" scoped>
.preview {
  padding: 0 1rem 0 1rem;
  border: 1px solid #dddddd;
}
</style>
